/**
 * The driver's day, on one screen.
 *
 * Requested 2 Oct: a dashboard showing the beat, the route, the invoices and
 * what is on the van.
 *
 * None of this is new information — all of it was reachable through four
 * screens and six taps. That is exactly why it was worth building: a driver who
 * has to go looking does not look. He works from what is in front of him, and
 * what was in front of him was a list of stop names with no amounts, no van
 * count, and no way to tell how much of the day was left.
 *
 * The numbers chosen are the ones he would otherwise work out on paper: stops
 * remaining, money still to collect, and what is actually in the vehicle.
 */

import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import { CustomerName } from '../../components/CustomerName';
import {
  Banner, Button, Card, Chip, Empty, ErrorState, Money, Skeleton, Tile,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus, usePoll } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { piecesAndPacks } from '../../lib/format';

interface Stop {
  dispatchId: string;
  invoiceId: string;
  sequence: number;
  status: string;
  shopName: string;
  invoiceNumber: string;
  balancePaise: number;
}

interface Sheet {
  id: string;
  number: string;
  status: string;
  vehicle: string | null;
  beat: string | null;
  totalStops: number;
  stops: Stop[];
}

interface DriverDashboard {
  dayStarted: boolean;
  vehicle: string | null;
  sheets: Sheet[];
  totals: {
    stops: number;
    delivered: number;
    pending: number;
    outstandingPaise: number;
  };
  vanStock: {
    skuCode: string;
    productName: string;
    quantity: number;
    packUom: string | null;
    packQty: number | null;
  }[];
  soldByOthers: { invoice_number: string; shop_name: string; sold_by: string | null }[];
}

interface AssignedOrder {
  id: string;
  order_number: string;
  net_amount_paise: number;
  shop_name: string;
  address_line1: string | null;
  legal_name: string;
  trade_name: string | null;
  customer_classification: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  balance_amount_paise: number | null;
}

export default function DriverDashboard() {
  const t = useT();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery<DriverDashboard>('/api/field/driver/dashboard');
  useRefreshOnFocus(refetch);

  /**
   * Orders routed to this driver at booking (Delivered by = driver), delivered
   * by invoice through the direct POD. Polled every 15s so a new one appears,
   * and is announced, while the driver has the app open; the push notification
   * covers the app being closed.
   */
  const assigned = useQuery<{ items: AssignedOrder[] }>('/api/orders/assigned');
  useRefreshOnFocus(assigned.refetch);
  // Visibility-aware poll (pauses in a backgrounded tab) instead of a raw timer.
  usePoll(assigned.refetch, 15000);

  // New-order audio is handled app-wide by the Shell's watcher (one summary per
  // batch), so this screen no longer announces per order — it just lists them.

  if (loading && !data) {
    return <Page title="My route"><Skeleton rows={3} height={90} /></Page>;
  }
  if (error && !data) {
    return <Page title="My route"><ErrorState message={error.message} onRetry={refetch} /></Page>;
  }

  const d = data;

  return (
    <Page title="My route" subtitle={d?.vehicle ?? undefined}>
      <div className="stack">
        {/*
          The day has to be open before any of this means anything. Said here
          rather than showing an empty dashboard, which reads as "no work today"
          — a driver has gone home on less.
        */}
        {!d?.dayStarted && (
          <Banner tone="warn" title="Your day is not open">
            <span className="t-sm">Start the day on My Day to pick your vehicle.</span>
          </Banner>
        )}

        {/*
          NEW ORDERS ROUTED TO THIS DRIVER.

          Orders booked "Delivered by: driver" for a shop on a beat this driver
          is out on today. Delivered by invoice through the direct POD, so they
          are not dispatch stops. Shown first — a new order is the most urgent
          thing on the screen — and announced aloud when it arrives.
        */}
        {(assigned.data?.items.length ?? 0) > 0 && (
          <Card flush>
            <div className="card__header"><h3>New orders to deliver</h3></div>
            <div className="list">
              {assigned.data!.items.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="list__item"
                  disabled={!o.invoice_id}
                  onClick={() => { if (o.invoice_id) navigate(`/pod/direct/${o.invoice_id}`); }}
                >
                  <div className="grow" style={{ minWidth: 0 }}>
                    {/* Name wraps rather than clipping, and carries the customer's
                        classification colour (blacklist struck through). */}
                    <CustomerName name={o.trade_name || o.legal_name || o.shop_name} classification={o.customer_classification} className="t-semi" />
                    <div className="t-xs t-muted t-clip">
                      {o.shop_name}{o.address_line1 ? ` · ${o.address_line1}` : ''}
                    </div>
                    <div className="t-xs t-muted">{o.invoice_number ?? o.order_number}</div>
                  </div>
                  {/* The order's VALUE, not its outstanding balance — a paid
                      order still shows what it is worth, not ₹0. */}
                  <Money paise={o.net_amount_paise} className="t-sm" />
                </button>
              ))}
            </div>
          </Card>
        )}

        {d && d.sheets.length === 0 && (
          <Empty
            title="Nothing loaded yet"
            hint="When the godown raises your load sheet it appears here."
            action={(
              <Button variant="ghost" onClick={() => navigate('/load-sheets')}>
                All load sheets
              </Button>
            )}
          />
        )}

        {d && d.sheets.length > 0 && (
          <>
            <div className="fieldgroup fieldgroup--blue">
              <h3 className="fieldgroup__title">Today's run</h3>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <Tile tone="blue" label="Stops left" value={String(d.totals.pending)} />
              <Tile tone="teal" label="Delivered" value={`${d.totals.delivered}/${d.totals.stops}`} />
            </div>
          </>
        )}

        {/*
          The full load-sheet list, reachable whether or not today is empty.
          This link used to sit inside the "nothing loaded yet" empty state, so
          the moment a driver had one sheet, every earlier sheet — and the
          load-in button for yesterday's van — became unreachable from
          anywhere in the app.
        */}
        {d && d.sheets.length > 0 && (
          <Button variant="ghost" block onClick={() => navigate('/load-sheets')}>
            All load sheets
          </Button>
        )}

        {d && d.totals.outstandingPaise > 0 && (
          <>
            <div className="fieldgroup fieldgroup--green">
              <h3 className="fieldgroup__title">Money</h3>
            </div>
            <Tile
              tone="green"
              label="Still to collect"
              value={<Money paise={d.totals.outstandingPaise} />}
              meta={`On the ${d.totals.pending} ${d.totals.pending === 1 ? 'stop' : 'stops'} not yet done`}
            />
          </>
        )}

        {/* --- The route, stop by stop, with what each one owes ------------- */}
        {(d?.sheets ?? []).map((sheet) => (
          <Card key={sheet.id} flush>
            <div className="card__header">
              <h3>{sheet.number}</h3>
              <div className="row" style={{ gap: 6 }}>
                {sheet.beat && <Chip outline>{sheet.beat}</Chip>}
                <Chip tone={sheet.status === 'reconciled' ? 'ok' : undefined}>{sheet.status}</Chip>
              </div>
            </div>

            {sheet.stops.length === 0 ? (
              <div className="list__item list__item--static">
                {/*
                  NOT "they appear when the route starts" — that was false and
                  it sent drivers to press Start expecting deliveries to show
                  up. Stops are created when the office raises the sheet, so
                  an empty list means the sheet was raised without invoices,
                  and starting the route will not conjure any.
                */}
                <span className="t-sm t-muted">
                  No drops on this sheet. The office raises them with the load —
                  call them if you were expecting deliveries.
                </span>
              </div>
            ) : (
              <div className="list">
                {sheet.stops.map((stop) => (
                  <button
                    key={stop.dispatchId}
                    type="button"
                    className="list__item"
                    onClick={() => navigate(`/pod/${stop.dispatchId}`)}
                  >
                    <span className="t-xs t-muted" style={{ width: 20 }}>{stop.sequence}</span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="t-semi t-clip">{stop.shopName}</div>
                      <div className="t-xs t-muted">{stop.invoiceNumber}</div>
                    </div>
                    <div className="stack-1" style={{ alignItems: 'flex-end', gap: 2 }}>
                      <Money paise={stop.balancePaise} className="t-sm" />
                      {stop.status === 'delivered'
                        ? <Chip tone="ok">{t('app.done')}</Chip>
                        : stop.status === 'failed'
                          ? <Chip tone="danger">Failed</Chip>
                          : null}
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="list__item list__item--static">
              <Button
                size="sm" variant="ghost"
                onClick={() => navigate(`/routes/${sheet.id}`)}
              >
                Open route
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={() => navigate(`/routes/${sheet.id}/extra-box`)}
              >
                Sell extra stock
              </Button>
            </div>
          </Card>
        ))}

        {/* --- What is actually on the van --------------------------------- */}
        {(d?.vanStock ?? []).length > 0 && (
          <Card flush>
            <div className="card__header"><h3>In the vehicle</h3></div>
            <div className="list">
              {d!.vanStock.map((v) => (
                <div key={v.skuCode} className="list__item list__item--static">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="t-sm t-semi t-clip">{v.productName}</div>
                    <div className="t-xs t-muted">{v.skuCode}</div>
                  </div>
                  <div className="stack-1" style={{ alignItems: 'flex-end', gap: 0 }}>
                    <span className="t-semi">{v.quantity}</span>
                    {/*
                      Boxes AND pieces, requested 2 Oct.

                      A driver counts cartons. "288" tells him nothing he can
                      check against the van floor; "12 box + 0" he can verify
                      by looking.
                    */}
                    {v.packQty && v.packQty > 1 && (
                      <span className="t-xs t-muted">
                        {piecesAndPacks(v.quantity, {
                          baseUom: 'PCS', packUom: v.packUom, packQty: v.packQty,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/*
          Sales somebody else made off this van today.

          Without this the driver reconciles at close against stock that left
          without his knowledge, and the variance is his to explain.
        */}
        {(d?.soldByOthers ?? []).length > 0 && (
          <Card flush>
            <div className="card__header"><h3>Sold from your van by others</h3></div>
            <div className="list">
              {d!.soldByOthers.map((s) => (
                <div key={s.invoice_number} className="list__item list__item--static">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="t-sm t-semi t-clip">{s.shop_name}</div>
                    <div className="t-xs t-muted">{s.invoice_number}</div>
                  </div>
                  <span className="t-xs t-muted">{s.sold_by ?? 'Office'}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
